import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Spinner } from 'react-bootstrap';
import { getAllOrganisationData } from '../utils/api/organisationAPI';
import { registerIntegration } from '../utils/api/integrationAPI.js'; 
import { motion } from 'framer-motion';

const AddIntegrationLayer = () => {
    const [formData, setFormData] = useState({
        name: '',
        api_key: '',
        organizationId: '',
        type: '',
        external_api_public_key: '',
        status: true,
    });

    const [organizations, setOrganizations] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        getAllOrganisationData({ setIsLoading: () => {} })
            .then(data => setOrganizations(data.organizations || []))
            .catch(e => console.error(e));
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);
            const res = await registerIntegration(formData);
            toast.success(res.message || 'Integration added successfully');
            setFormData({
                name: '',
                api_key: '',
                organizationId: '',
                type: '',
                external_api_public_key: '',
                status: true,
            });
        } catch (e) {
            toast.error(e.message || 'Something went wrong');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="col-lg-12"
        >
            <div className="card" style={cardStyle}>
                <div className="card-header" style={cardHeaderStyle}>
                    <h5 className="card-title mb-0" style={titleStyle}>
                        <Icon icon="mdi:link-variant-plus" style={{ verticalAlign: 'middle', marginRight: '10px' }} width="22" height="22" />
                        Add Integration
                    </h5>
                </div>
                <div className="card-body" style={cardBodyStyle}>
                    <form className="row gy-4" onSubmit={handleSubmit}>

                        {/* Name */}
                        <div className="col-md-6">
                            <label className="form-label" style={labelStyle}>Name</label>
                            <div style={{ position: 'relative' }}>
                                <Icon icon="mdi:label" width="18" height="18" style={iconStyle} />
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter integration name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    style={inputStyle}
                                    required
                                />
                            </div>
                        </div>

                        {/* API Key */}
                        <div className="col-md-6">
                            <label className="form-label" style={labelStyle}>API Key</label>
                            <div style={{ position: 'relative' }}>
                                <Icon icon="mdi:key" width="18" height="18" style={iconStyle} />
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter API Key"
                                    value={formData.api_key}
                                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                                    style={inputStyle}
                                    required
                                />
                            </div>
                        </div>

                        {/* Type */}
                        <div className="col-md-6">
                            <label className="form-label" style={labelStyle}>Type</label>
                            <div style={{ position: 'relative' }}>
                                <Icon icon="mdi:cog" width="18" height="18" style={iconStyle} />
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter type"
                                    value={formData.type}
                                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                    style={inputStyle}
                                    required
                                />
                            </div>
                        </div>

                        {/* External API Public Key */}
                        <div className="col-md-6">
                            <label className="form-label" style={labelStyle}>External API Key</label>
                            <div style={{ position: 'relative' }}>
                                <Icon icon="mdi:key" width="18" height="18" style={iconStyle} />
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter External API Key"
                                    value={formData.external_api_public_key}
                                    onChange={(e) => setFormData({ ...formData, external_api_public_key: e.target.value })}
                                    style={inputStyle}
                                    required
                                />
                            </div>
                        </div>

                        {/* Organization Dropdown */}
                        <div className="col-md-6">
                            <label className="form-label" style={labelStyle}>Organization</label>
                            <div className="position-relative">
                                <Icon icon="mdi:office-building" width="18" height="18" style={iconStyle} />
                                <select
                                    value={formData.organizationId}
                                    onChange={(e) => setFormData({ ...formData, organizationId: e.target.value })}
                                    className="form-control"
                                    style={selectStyle}
                                    required
                                >
                                    <option value="">Select Organization</option>
                                    {organizations.map(org => (
                                        <option key={org.id} value={org.id}>{org.name}</option>
                                    ))}
                                </select>
                                <Icon icon="mdi:chevron-down" width="18" height="18" style={dropdownIconStyle} />
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="col-12" style={footerStyle}>
                            <button
                                type="submit"
                                className="btn btn-primary-600"
                                style={submitBtnStyle}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Submit'}
                            </button>
                        </div>

                    </form>
                </div>
            </div>
        </motion.div>
    );
};

// Reuse styles from your original setup
const labelStyle = {
    fontWeight: '500',
    marginBottom: '8px',
    color: '#344767',
    fontSize: '14px'
};

const iconStyle = {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#6c757d'
};

const inputStyle = {
    paddingLeft: '40px',
    height: '45px',
    borderColor: '#e1e5eb',
    fontSize: '14px'
};

const selectStyle = {
    paddingLeft: '40px',
    height: '45px',
    appearance: 'none',
    borderColor: '#e1e5eb',
    fontSize: '14px'
};

const dropdownIconStyle = {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    color: '#6c757d'
};

const footerStyle = {
    background: '#ffffff',
    padding: '20px 25px',
    borderTop: '1px solid #eaeaea',
    borderBottomLeftRadius: '10px',
    borderBottomRightRadius: '10px',
    display: 'flex',
    justifyContent: 'flex-end'
};

const submitBtnStyle = {
    padding: '12px 30px',
    fontWeight: '500',
    fontSize: '15px',
    borderRadius: '6px',
    background: 'var(--primary-500)',
    border: 'none',
    boxShadow: '0 4px 8px rgba(98, 89, 202, 0.3)',
    transition: 'all 0.3s ease',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
};

const cardStyle = {
    borderRadius: '10px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    border: 'none'
};

const cardHeaderStyle = {
    background: '#f8f9fa',
    borderBottom: '1px solid #eaeaea',
    padding: '20px 25px'
};

const cardBodyStyle = {
    padding: '30px 25px',
    background: '#ffffff'
};

const titleStyle = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#344767'
};

export default AddIntegrationLayer;
